import {
    Box,
    Button,
    capitalize,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import DataTable from "../data/DataTable";
import {Nachtrag, NachtragDaten} from "../../api";
import {Delete} from "@mui/icons-material";
import {
    useDeleteBaustelleNachtrag,
    useUpdateBaustelleNachtrag,
} from "../../client/hooks";
import {useQueryClient} from "react-query";
import {formatAsMoney, formatDate} from "../../formatterHelpers";
import useCustomFilters from "../data/useCustomFilters";

export type BaustellenFilter = {
    baustellenId: string;
    search?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<BaustellenFilter>, "filter"> &
    Partial<Pick<DataRequest<BaustellenFilter>, "filter">>;

const tableName: string = "nachtraege"

export default function NachtraegeDataTable({...input}: Props) {
    const {id: baustellenId = ""} = useParams<{
        id: string;
    }>();
    const request = useDataRequest<BaustellenFilter>({
        filter: {baustellenId: baustellenId},
        ...input,
    });

    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp request={request} filteredStateFilter={filteredState}/>
            <BaustellenResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<BaustellenFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<BaustellenFilter>({
        tableName: tableName,
        defaultFilter: {
            baustellenId: request.filter.baustellenId,
            search: "",
        }
    });
    const navigate = useNavigate();
    const {id: baustellenId = ""} = useParams<{
        id: string;
    }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT();

    useEffect(() => {
        setFilter({
            baustellenId: currentCustomFilter.baustellenId,
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{marginLeft: 2}}
                        onClick={() => {
                            navigate("/nachtrag/" + baustellenId + "/neu-nachtrag");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{position: "absolute", top: 0, right: 0}}
                        onClick={() => {
                            navigate("/nachtrag/" + baustellenId + "/neu-nachtrag");
                        }}
                    >
                        {capitalize(translation("create-new"))}
                    </Button>
                </>
            )}
        </Box>
    );
}

function BaustellenResults({
                               request,
                               setFilteredState,
                           }: {
    request: DataRequestState<BaustellenFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<BaustellenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const getApi = useGetApi();
    const loadNachtrag: DataLoader<BaustellenFilter, Nachtrag> = useCallback(
        async (params) =>
            (await getApi()).baustellen.sucheBaustellenNachtraege({
                ...params,
            }),
        [getApi]
    );

    const queryClient = useQueryClient();
    const updateNachtrag = useUpdateBaustelleNachtrag();

    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadNachtrag}
            name={tableName}
            setFiltered={setFilteredState}
            disableRowSelectionOnClick={true}
            processRowUpdate={(updatedRow: any, originalRow: any) => {
                const nachtragData: NachtragDaten = {
                    summe: updatedRow.summe,
                    bemerkung: updatedRow.bemerkung,
                    datum: updatedRow.datum,
                };
                updateNachtrag(request.filter.baustellenId, originalRow.id, nachtragData).then(() => {
                    queryClient.invalidateQueries([tableName]);
                    setTimeout(() => {
                        queryClient.invalidateQueries(["baustellen"]);
                    }, 500);
                });
                return updatedRow;
            }}
        />
    );
}

function useColumns(): Array<GridColDef<Nachtrag>> {
    const {t: translation} = useT("customer");
    const deleteNachtrag = useDeleteBaustelleNachtrag();
    const {id: baustellenId = ""} = useParams<{
        id: string;
    }>();
    const queryClient = useQueryClient();

    return useMemo(
        () => [
            {
                field: "summe",
                headerName: capitalize(translation("total_one")),
                align: "right",
                headerAlign: "right",
                type: "number",
                editable: true,
                flex: 0.33,
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "bemerkung",
                headerName: capitalize(translation("bemerkung")),
                headerAlign: "center",
                editable: true,
                flex: 1,
            },
            {
                field: "datum",
                headerName: capitalize(translation("date")),
                headerAlign: "right",
                renderCell: (params) => {
                    const date = new Date(params.value);
                    return formatDate(date.toDateString());
                },
            },
            {
                field: "buttons",
                headerName: "",
                headerAlign: "right",
                renderCell: (params) => {
                    return (
                        <Button
                            startIcon={<Delete/>}
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                deleteNachtrag(baustellenId, params.row.id).then(() => {
                                    queryClient.invalidateQueries([tableName]);
                                    setTimeout(() => {
                                        queryClient.invalidateQueries(["baustellen"]);
                                    }, 500);
                                });
                            }}
                        />
                    );
                },
            },
        ],
        [baustellenId, deleteNachtrag, queryClient, translation]
    );
}
